/* =========================================================================
 * Trust — homepage section.
 *
 * Answers the researcher's primary question, called out in the brief:
 *   "Why should I trust this data?"
 *
 * Four pillars rendered as a 2×2 grid on desktop, stacked on mobile. Heavy
 * left-aligned typography (no centered marketing fluff) to read more like
 * documentation than a sales page — matches the brief's scientific aesthetic.
 * ========================================================================= */

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

const PILLARS = [
  {
    icon: "verified",
    title: "Data validation",
    body: "Every upload is checksummed and tagged with device, area, sensor, and timestamp. Outliers are flagged for review before any aggregation.",
  },
  {
    icon: "tune",
    title: "Quality controls",
    body: "GPS speed jumps over 200 m/s are dropped. Accelerometer shake events use a 5-second cooldown to prevent duplicate counts. Each sensor has documented filters.",
  },
  {
    icon: "policy",
    title: "Transparent methodology",
    body: "Aggregation rules, retention policy, and dataset schemas are documented and version-controlled. Researchers see exactly how the numbers they consume were derived.",
  },
  {
    icon: "lock",
    title: "Contributor privacy",
    body: "Devices contribute anonymously. No personal identifiers are attached to readings. Location data is bucketed into 250 km zones before it leaves the device.",
  },
];

export function Trust() {
  return (
    <MKBox
      component="section"
      py={{ xs: 8, md: 12 }}
      px={{ xs: 2, md: 4 }}
      sx={({ palette }) => ({
        borderTop: `1px solid ${palette.grey[200]}`,
        background: palette.dark.main,
        color: palette.white.main,
      })}
    >
      <Container>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="flex-start">
          <Grid item xs={12} md={5}>
            <MKTypography
              variant="overline"
              sx={({ palette }) => ({
                color: palette.info.main,
                letterSpacing: "0.1em",
                fontWeight: 700,
              })}
            >
              Trust & credibility
            </MKTypography>
            <MKTypography
              variant="h2"
              mt={1}
              sx={({ palette }) => ({
                color: palette.white.main,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                lineHeight: 1.15,
              })}
            >
              Built for scientific rigor
            </MKTypography>
            <MKTypography
              variant="body1"
              mt={3}
              sx={({ palette }) => ({
                color: palette.grey[300],
                lineHeight: 1.6,
              })}
            >
              A researcher&apos;s first question is &ldquo;why should I trust this data?&rdquo;
              These are the answers — validation, quality control, transparency, and an audit trail
              from device to dataset.
            </MKTypography>
          </Grid>

          <Grid item xs={12} md={7}>
            <Grid container spacing={3}>
              {PILLARS.map((p) => (
                <Grid item xs={12} sm={6} key={p.title}>
                  <MKBox
                    sx={{
                      p: 3,
                      height: "100%",
                      borderRadius: 3,
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                      background: "rgba(255, 255, 255, 0.03)",
                      transition: "border-color 0.2s ease, background 0.2s ease",
                      "&:hover": {
                        borderColor: "rgba(56, 189, 248, 0.3)",
                        background: "rgba(255, 255, 255, 0.05)",
                      },
                    }}
                  >
                    <MKBox
                      sx={({ palette }) => ({
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        display: "grid",
                        placeItems: "center",
                        background: "rgba(56, 189, 248, 0.15)",
                        color: palette.info.main,
                        mb: 2,
                      })}
                    >
                      <Icon fontSize="small">{p.icon}</Icon>
                    </MKBox>
                    <MKTypography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={({ palette }) => ({ color: palette.white.main })}
                    >
                      {p.title}
                    </MKTypography>
                    <MKTypography
                      variant="body2"
                      mt={1}
                      sx={({ palette }) => ({ color: palette.grey[400], lineHeight: 1.55 })}
                    >
                      {p.body}
                    </MKTypography>
                  </MKBox>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}
